// ═══════════════════════════════════════════════════════════════════════
// Congés › Droits à congé › un salarié (Lot 3, moteur Congés) — fiche DRH.
// Compteurs, droits ouverts (« Voir le calcul » : instantané figé à l'ouverture), droits théoriques à
// confirmer, période en acquisition, registre des mouvements, reprise des soldes, ancienneté reprise,
// date d'entrée, régularisations et préparation de la sortie (en jours, aucun montant).
// Les droits viennent du serveur (droits_utilisateur) ; jamais d'action sur soi ; toute proposition est
// décidée par une autre personne : le serveur le contrôle et son message s'affiche tel quel.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CalendarRange, Calculator, CheckCircle2, XCircle, ArrowRightLeft, History, UserCheck, CalendarClock, Scale, LogOut, Search,
  Plus, Hourglass, ListChecks, BadgeCheck, Trash2,
} from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, BandeauInfo, Badge, Onglets, Squelette, EtatVide } from '../../design/composants';
import { useChargement, Fenetre, Champ, PiedFenetre } from '../temps/reglagesCommun';
import {
  lireDroits, ecrireDroits, messageErreur, nombre, jours, date, aujourdhui, BadgeEtatCalcul, BadgeQualiteDate, libelleQualiteDate,
  QUALITES_DATE, SOURCES_QUALITE_DATE, RESERVES, ALERTES,
} from './DroitsCongesPage';

// ─── Libellés ────────────────────────────────────────────────────────────────────────────────
const NATURES = {
  OUVERTURE_DROIT: 'Ouverture du droit', MAJORATION_ANCIENNETE: 'Majoration pour ancienneté', MAJORATION_FAMILLE: 'Majoration mère de famille',
  MAJORATION_JEUNE: 'Majoration jeune travailleur', REPRISE_INITIALE: 'Reprise de l\'ancien solde', REPORT_ENTRANT: 'Report reçu d\'un droit précédent',
  REPORT_SORTANT: 'Report vers le droit suivant', REGULARISATION: 'Régularisation', AJUSTEMENT: 'Ajustement', PRISE: 'Congé pris',
  RETOUR: 'Jours rendus (congé interrompu ou annulé)',
};
const STATUTS_DROIT = { OUVERT: ['succes', 'Ouvert'], SOLDE: ['neutre', 'Entièrement pris'], ECHEANCE_DEPASSEE: ['danger', 'Échéance dépassée'] };
const QUALITES_DROIT = { CERTAIN: 'Calcul certain', CONFIRME_SOUS_RESERVE: 'Confirmé par la RH sous réserve', REPRIS: 'Repris d\'un ancien solde' };
const ORIGINES_DROIT = { CALCUL: 'Calculé', REPRISE: 'Repris' };
const STATUTS_PROPOSITION = { PROPOSEE: ['alerte', 'En attente de décision'], VALIDEE: ['succes', 'Validée'], REJETEE: ['neutre', 'Rejetée'] };
const SOURCES_REPRISE = { DOSSIER: 'Dossier du salarié', BULLETIN: 'Bulletin de paie', ATTESTATION: 'Attestation', DECLARATION: 'Déclaration', AUTRE: 'Autre source' };
const ORIGINES_ANCIENNETE = { SERAS: 'SERAS', CONTRAT_ANTERIEUR: 'Contrat antérieur', AUTRE_EMPLOYEUR: 'Autre employeur', AUTRE: 'Autre' };
const CAUSES_REGUL = { DATE_ENTREE: 'Date d\'entrée modifiée', ANCIENNETE_REPRISE: 'Ancienneté reprise décidée', DONNEES: 'Données du salarié modifiées' };
const NIVEAUX = { LOI: 'Loi ou règlement', CONVENTION: 'Convention collective', ACCORD: 'Accord d\'entreprise', SOGAS: 'Politique SOGAS' };
const STATUTS_REGLE = { CONFIRME: ['succes', 'Confirmée'], DECISION_SOGAS: ['info', 'Décision SOGAS'], A_CONFIRMER: ['alerte', 'À confirmer'] };
const QUALITES_SAISIE = ['CONFIRMEE', 'REPRISE_APPROXIMATIVE', 'A_CONFIRMER', 'INCONNUE'];

/** Remplace dans un texte du serveur les codes connus (source, cause, origine) par leur libellé. */
const CODES_TEXTE = { ...SOURCES_REPRISE, ...CAUSES_REGUL, ...ORIGINES_ANCIENNETE };
const enClair = (t) => String(t || '').replace(/\b([A-Z][A-Z_]{2,})\b/g, (m, c) => (CODES_TEXTE[c] && c !== 'SERAS' ? CODES_TEXTE[c].toLowerCase() : m));
const signe = (n) => (Number(n) > 0 ? `+${jours(n)}` : jours(n));
const nombreSaisi = (v) => Number(String(v).replace(',', '.').trim());

const BadgeCode = ({ table, code }) => {
  const [ton, libelle] = table[code] || ['neutre', 'Inconnu'];
  return <Badge ton={ton} point>{libelle}</Badge>;
};

// ─── Petits composants ───────────────────────────────────────────────────────────────────────
function Tuile({ libelle, valeur, aide, ton }) {
  return (
    <div className="rh-carte" style={{ padding: 12, margin: 0, flex: '1 1 150px', minWidth: 0 }}>
      <div style={{ fontSize: 13, color: 'var(--sg-texte-2)' }}>{libelle}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: ton ? `var(--sg-${ton}, inherit)` : undefined, overflowWrap: 'anywhere' }}>{valeur}</div>
      {aide && <div style={{ fontSize: 12, color: 'var(--sg-texte-3)' }}>{aide}</div>}
    </div>
  );
}

const Defilant = ({ children, libelle }) => (
  <div style={{ overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch' }} tabIndex={0} role="region" aria-label={libelle}>{children}</div>
);

/** Liste « clé : valeur » responsive. */
function Lignes({ lignes }) {
  return (
    <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: '8px 20px', margin: 0, fontSize: 14 }}>
      {lignes.filter(Boolean).map(([t, v]) => (
        <div key={t} style={{ minWidth: 0 }}>
          <dt style={{ fontSize: 12, color: 'var(--sg-texte-3)' }}>{t}</dt>
          <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Étapes d'un calcul (« Pourquoi ce résultat ? »). */
function Etapes({ etapes }) {
  if (!etapes || !etapes.length) return <p style={{ margin: 0, fontSize: 14 }}>Aucune explication enregistrée.</p>;
  return (
    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, display: 'grid', gap: 3 }}>
      {etapes.map((e, i) => (
        <li key={`${e.cle}-${i}`}>
          {e.libelle} : <strong>{enClair(e.valeur)}</strong>{e.detail ? ` — ${enClair(e.detail)}` : ''}
          {e.regle && (e.regle.reference || e.regle.libelle) && (
            <span style={{ color: 'var(--sg-texte-3)', fontSize: 13 }}> (source : {e.regle.reference || e.regle.libelle}{e.regle.statut === 'A_CONFIRMER' ? ', règle à confirmer' : ''})</span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Réserves d'un calcul, en phrases. */
function ListeReserves({ reserves }) {
  if (!reserves || !reserves.length) return null;
  return (
    <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 14 }}>
      {reserves.map((r, i) => (
        <li key={`${r.code}-${i}`}>{RESERVES[r.code] || 'Point à vérifier.'}
          {r.message && <span style={{ color: 'var(--sg-texte-2)', fontSize: 13 }}> {enClair(r.message)}</span>}
        </li>
      ))}
    </ul>
  );
}

/** Fenêtre à motif obligatoire (décision, confirmation, report). */
function FenetreMotif({ titre, Icone, aide, resume, min = 3, libelleBouton, variante = 'primaire', onFermer, onConfirmer }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const court = motif.trim().length < min;
  return (
    <Fenetre titre={titre} Icone={Icone} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {resume}
      {aide && <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>{aide}</p>}
      <Champ libelle="Motif" requis aide={min > 3 ? `${min} caractères au moins.` : undefined}>
        <textarea className="rh-champ-saisie" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />
      </Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante={variante} Icone={Icone} disabled={enCours || court}
          onClick={async () => {
            setEnCours(true); setErreur('');
            try { await onConfirmer(motif.trim()); } catch (e) { setErreur(messageErreur(e)); setEnCours(false); }
          }}>{libelleBouton || titre}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

/** Formulaire de proposition générique : erreur serveur affichée, bouton bloqué pendant l'envoi. */
function FenetreFormulaire({ titre, Icone, pret, onFermer, onEnvoyer, children, large = false, libelleBouton = 'Proposer' }) {
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  return (
    <Fenetre titre={titre} Icone={Icone} onFermer={onFermer} large={large}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {children}
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="primaire" Icone={Icone} disabled={enCours || !pret}
          onClick={async () => {
            setEnCours(true); setErreur('');
            try { await onEnvoyer(); } catch (e) { setErreur(messageErreur(e)); setEnCours(false); }
          }}>{libelleBouton}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

// ─── « Voir le calcul » : instantané figé d'un droit ─────────────────────────────────────────
const valeurRegle = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return nombre(v);
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return 'Barème';
  return 'Tableau';
};

// Recette scénario 1 (anomalie 3) : ce que le droit est devenu depuis son ouverture — état ACTUEL, distinct de
// l'instantané figé ci-dessus : droit initial, mouvements, pris, planifiés, rendus, restant, demandes liées.
const NATURES_MOUVEMENT = {
  OUVERTURE_DROIT: 'Ouverture du droit', MAJORATION_ANCIENNETE: 'Majoration d\'ancienneté', MAJORATION_JEUNE: 'Majoration jeune', MAJORATION_MERE: 'Majoration mère',
  REPRISE_INITIALE: 'Reprise d\'un ancien solde', PRISE: 'Congé pris ou planifié', RETOUR: 'Jours rendus', REPORT_ENTRANT: 'Report reçu',
  REPORT_SORTANT: 'Report vers le droit suivant', REGULARISATION: 'Régularisation', AJUSTEMENT: 'Ajustement',
};
const ETATS_DEMANDE = { PRIS: 'pris', PLANIFIE: 'planifié', RENDU: 'rendu (annulé)' };
function UtilisationDroit({ u }) {
  if (!u) return null;
  return (
    <>
      <h3 style={{ fontSize: 15, margin: '16px 0 6px' }}>Utilisation de ce droit (état actuel)</h3>
      <Lignes lignes={[
        ['Droit initial', jours(u.initial)],
        u.reporte_entrant ? ['Report reçu', jours(u.reporte_entrant)] : null,
        u.regularise ? ['Régularisé', signe(u.regularise)] : null,
        ['Jours pris', jours(u.pris)],
        ['Jours planifiés', jours(u.planifies)],
        ['Jours rendus', jours(u.rendus)],
        u.reporte_sortant ? ['Reportés vers le droit suivant', jours(u.reporte_sortant)] : null,
        ['Solde restant', jours(u.restant)],
      ]} />
      {(u.demandes || []).length > 0 && (
        <Defilant libelle="Demandes liées à ce droit">
          <table className="rh-tableau" style={{ fontSize: 13, minWidth: 560 }}>
            <thead><tr><th>Demande</th><th>Statut</th><th>Utilisés</th><th>Rendus</th><th>Effet</th></tr></thead>
            <tbody>{u.demandes.map((x) => (
              <tr key={x.conge_id}>
                <td>{x.du ? `du ${date(x.du)} au ${date(x.au)}` : 'Demande'}</td><td>{x.statut || '—'}</td>
                <td>{jours(x.utilises)}</td><td>{jours(x.rendus)}</td><td>{ETATS_DEMANDE[x.etat] || x.etat}</td>
              </tr>
            ))}</tbody>
          </table>
        </Defilant>
      )}
      {(u.mouvements || []).length > 0 && (
        <Defilant libelle="Mouvements de ce droit">
          <table className="rh-tableau" style={{ fontSize: 13, minWidth: 560 }}>
            <thead><tr><th>Date</th><th>Mouvement</th><th>Jours</th><th>Motif</th></tr></thead>
            <tbody>{u.mouvements.map((m, i) => (
              <tr key={i}><td>{date(m.date)}</td><td>{NATURES_MOUVEMENT[m.nature] || m.nature}</td><td>{signe(m.jours)}</td><td>{m.motif || '—'}</td></tr>
            ))}</tbody>
          </table>
        </Defilant>
      )}
    </>
  );
}

function FenetreCalcul({ droit, onFermer }) {
  const { donnees: x, erreur } = useChargement(() => lireDroits(`/droits/${droit.id}/explication`), [droit.id]);
  const etapes = x?.explication || [];
  const une = (cle) => etapes.find((e) => e.cle === cle);
  const toutes = (...cles) => etapes.filter((e) => cles.includes(e.cle));
  const d = x?.donnees || {};
  const periode = une('PERIODE');
  const base = une('BASE');
  const anc = une('ANCIENNETE');
  const majorations = toutes('JEUNE', 'MAJORATION');
  const nonAccordees = toutes('NON_APPLIQUEE');
  const reprise = une('REPRISE');
  const pack = x?.pack && x.pack.code ? `${x.pack.code} — version ${x.pack.version}` : 'Règles validées du Centre de configuration (hors pack)';
  const exclues = (d.suspensions_exclues || []).length;
  return (
    <Fenetre titre={`Calcul du droit ouvert le ${date(droit.ouverture)}`} Icone={Calculator} onFermer={onFermer} large>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!x && !erreur && <Squelette hauteur={180} />}
      {x && (
        <>
          <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
            Calcul enregistré le {date(x.fige_le)}, à l'ouverture du droit. Il ne change pas si une règle ou une donnée change ensuite :
            un écart passe par une régularisation.
          </p>
          <Lignes lignes={[
            ['Salarié', x.salarie],
            x.origine === 'REPRISE'
              ? ['Origine', reprise ? `${enClair(reprise.valeur)} — ${enClair(reprise.detail)}` : 'Droit repris d\'un ancien solde']
              : ['Date d\'entrée', `${date(d.date_entree)} — qualité : ${libelleQualiteDate(d.qualite_date_entree).toLowerCase()}${d.qualite_date_entree?.source ? ` (${SOURCES_QUALITE_DATE[d.qualite_date_entree.source] || ''})` : ''}`],
            d.debut_calcul && ['Début de période repris', date(d.debut_calcul)],
            ['Période de référence', `du ${date(x.periode_debut)} au ${date(x.ouverture)}`],
            x.origine !== 'REPRISE' && ['Service effectif', [periode?.detail || '—',
              exclues ? `${exclues} absence(s) non comptée(s)` : null,
              d.historique_complet === false ? 'historique antérieur inconnu : service présumé continu' : null].filter(Boolean).join(' ; ')],
            x.origine !== 'REPRISE' && ['Règles appliquées', pack],
            x.origine !== 'REPRISE' && ['Droit de base', `${jours(x.jours_base)}${base?.detail ? ` (${base.detail})` : ''}`],
            x.origine !== 'REPRISE' && ['Ancienneté à l\'ouverture', `${d.anciennete_ans === null || d.anciennete_ans === undefined ? '—' : `${d.anciennete_ans} an${d.anciennete_ans > 1 ? 's' : ''}`}${anc?.detail ? ` — ${anc.detail}` : ''}${d.anciennete?.statut === 'A_CONFIRMER' ? ' — à confirmer' : ''}`],
            x.origine !== 'REPRISE' && ['Majorations', majorations.length ? `${majorations.map((m) => `${m.libelle} ${m.valeur}`).join(' ; ')} — total ${jours(x.jours_majorations)}` : 'Aucune'],
            nonAccordees.length > 0 && ['Non accordées', nonAccordees.map((m) => `${m.libelle}${m.detail ? ` (${m.detail})` : ''}`).join(' ; ')],
            ['Droit ouvert', `${jours(x.jours_total)} le ${date(x.ouverture)}`],
            ['À prendre au plus tard', x.echeance ? date(x.echeance) : 'Pas d\'échéance calculée'],
            ['Report', [droit.reporte_entrant ? `${jours(droit.reporte_entrant)} reçus d'un droit précédent` : null,
              droit.reporte_sortant ? `${jours(droit.reporte_sortant)} reportés vers le droit suivant` : null].filter(Boolean).join(' ; ') || 'Aucun report constaté'],
            droit.regularise ? ['Régularisé', signe(droit.regularise)] : null,
            ['Pris ou planifiés', jours(droit.pris)],
            ['Restant', jours(droit.restant)],
            ['Qualité', QUALITES_DROIT[x.qualite] || 'Non précisée'],
          ]} />

          <UtilisationDroit u={x.utilisation} />

          {(x.reserves_acceptees || []).length > 0 && (
            <BandeauInfo ton="alerte" titre="Réserves acceptées à la confirmation">
              <ListeReserves reserves={x.reserves_acceptees} />
              {x.motif_confirmation && <p style={{ margin: '6px 0 0', fontSize: 14 }}>Motif de la confirmation : {x.motif_confirmation}</p>}
            </BandeauInfo>
          )}

          <h3 style={{ fontSize: 15, margin: '16px 0 6px' }}>Étapes du calcul</h3>
          <Etapes etapes={etapes} />

          <h3 style={{ fontSize: 15, margin: '16px 0 6px' }}>Sources des règles</h3>
          {(x.regles || []).length === 0 ? <p style={{ margin: 0, fontSize: 14 }}>Aucune règle enregistrée pour ce droit.</p> : (
            <Defilant libelle="Sources des règles">
              <table className="rh-tableau" style={{ fontSize: 13, minWidth: 640 }}>
                <thead><tr><th>Règle</th><th>Valeur</th><th>Niveau</th><th>Texte</th><th>Pack et version</th><th>Statut</th><th>Lue au</th></tr></thead>
                <tbody>{x.regles.map((r, i) => (
                  <tr key={`${r.code}-${r.version_id || i}`}>
                    <td>{r.libelle}</td><td>{valeurRegle(r.valeur)}</td><td>{NIVEAUX[r.niveau] || '—'}</td><td>{r.reference || '—'}</td>
                    <td>{r.pack && r.pack.code ? `${r.pack.code}, version ${r.pack.version}` : 'Hors pack'}</td>
                    <td><BadgeCode table={STATUTS_REGLE} code={r.statut} /></td><td>{date(r.lue_le)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </Defilant>
          )}
        </>
      )}
      <PiedFenetre><Bouton onClick={onFermer}>Fermer</Bouton></PiedFenetre>
    </Fenetre>
  );
}

// ─── Formulaires de proposition ──────────────────────────────────────────────────────────────
function FenetreReprise({ employeId, onFermer, onTermine }) {
  const [f, setF] = useState({ date_situation: aujourdhui(), debut_periode_en_cours: '', source: 'DOSSIER', reference: '', motif: '' });
  const [lignes, setLignes] = useState([{ ouverture: '', echeance: '', jours: '' }]);
  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const majLigne = (i, k) => (e) => setLignes(lignes.map((l, j) => (j === i ? { ...l, [k]: e.target.value } : l)));
  const remplies = lignes.filter((l) => l.ouverture || l.jours || l.echeance);
  const lignesOk = remplies.every((l) => l.ouverture && nombreSaisi(l.jours) > 0);
  const pret = f.date_situation && f.debut_periode_en_cours && f.motif.trim().length >= 3 && lignesOk;
  return (
    <FenetreFormulaire titre="Proposer la reprise des soldes" Icone={History} large pret={pret} onFermer={onFermer}
      onEnvoyer={async () => {
        await ecrireDroits(`/salaries/${employeId}/reprises`, {
          ...f, droits: remplies.map((l) => ({ ouverture: l.ouverture, echeance: l.echeance || null, jours: nombreSaisi(l.jours) })),
        });
        onTermine('Reprise proposée : elle doit être validée par une autre personne habilitée.');
      }}>
      <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
        Indiquez la situation du salarié à une date donnée : le début de sa période en cours et les droits déjà ouverts qu'il n'a pas encore pris.
        L'ancien solde du logiciel n'est jamais repris tel quel.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <Champ libelle="Date de la situation" requis><input type="date" className="rh-champ-saisie" value={f.date_situation} onChange={maj('date_situation')} /></Champ>
        <Champ libelle="Début de la période en cours" requis aide="Au plus tard la date de la situation.">
          <input type="date" className="rh-champ-saisie" value={f.debut_periode_en_cours} onChange={maj('debut_periode_en_cours')} />
        </Champ>
      </div>
      <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
        <legend style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Droits ouverts non pris (cinq au plus)</legend>
        {lignes.map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ flex: '1 1 150px', fontSize: 13 }}>Ouvert le
              <input type="date" className="rh-champ-saisie" value={l.ouverture} onChange={majLigne(i, 'ouverture')} />
            </label>
            <label style={{ flex: '1 1 150px', fontSize: 13 }}>À prendre avant (facultatif)
              <input type="date" className="rh-champ-saisie" value={l.echeance} onChange={majLigne(i, 'echeance')} />
            </label>
            <label style={{ flex: '0 1 110px', fontSize: 13 }}>Jours
              <input type="text" inputMode="decimal" className="rh-champ-saisie" value={l.jours} onChange={majLigne(i, 'jours')} placeholder="ex. 12,5" />
            </label>
            <Bouton petit variante="discret" Icone={Trash2} aria-label={`Retirer le droit ${i + 1}`} onClick={() => setLignes(lignes.filter((_, j) => j !== i))} />
          </div>
        ))}
        {lignes.length < 5 && <Bouton petit Icone={Plus} onClick={() => setLignes([...lignes, { ouverture: '', echeance: '', jours: '' }])}>Ajouter un droit</Bouton>}
        {!lignesOk && <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--sg-danger)' }}>Chaque droit indique sa date d'ouverture et un nombre de jours positif.</p>}
      </fieldset>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <Champ libelle="Source" requis>
          <select className="rh-champ-saisie" value={f.source} onChange={maj('source')}>
            {Object.entries(SOURCES_REPRISE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Champ>
        <Champ libelle="Référence du document"><input className="rh-champ-saisie" value={f.reference} onChange={maj('reference')} /></Champ>
      </div>
      <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={2} value={f.motif} onChange={maj('motif')} /></Champ>
    </FenetreFormulaire>
  );
}

function FenetreAnciennete({ employeId, onFermer, onTermine }) {
  const [f, setF] = useState({ decision: 'REPRISE', date_depart: '', origine: 'SERAS', reference: '', date_effet: aujourdhui(), motif: '' });
  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pret = f.date_effet && f.motif.trim().length >= 3 && (f.decision === 'PAS_DE_REPRISE' || f.date_depart);
  return (
    <FenetreFormulaire titre="Proposer une décision d'ancienneté" Icone={UserCheck} pret={pret} onFermer={onFermer}
      onEnvoyer={async () => {
        await ecrireDroits(`/salaries/${employeId}/anciennete`, { ...f, date_depart: f.decision === 'REPRISE' ? f.date_depart : null });
        onTermine('Décision d\'ancienneté proposée : elle doit être validée par une autre personne habilitée.');
      }}>
      <Champ libelle="Décision" requis>
        <select className="rh-champ-saisie" value={f.decision} onChange={maj('decision')}>
          <option value="REPRISE">Reprendre une ancienneté antérieure</option>
          <option value="PAS_DE_REPRISE">Ne pas reprendre d'ancienneté (ancienneté depuis l'entrée)</option>
        </select>
      </Champ>
      {f.decision === 'REPRISE' && (
        <Champ libelle="Ancienneté comptée à partir du" requis>
          <input type="date" className="rh-champ-saisie" value={f.date_depart} onChange={maj('date_depart')} />
        </Champ>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <Champ libelle="Origine" requis>
          <select className="rh-champ-saisie" value={f.origine} onChange={maj('origine')}>
            {Object.entries(ORIGINES_ANCIENNETE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Champ>
        <Champ libelle="Date d'effet" requis><input type="date" className="rh-champ-saisie" value={f.date_effet} onChange={maj('date_effet')} /></Champ>
      </div>
      <Champ libelle="Référence (accord, contrat, courrier)"><input className="rh-champ-saisie" value={f.reference} onChange={maj('reference')} /></Champ>
      <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={2} value={f.motif} onChange={maj('motif')} /></Champ>
    </FenetreFormulaire>
  );
}

function FenetreQualiteDate({ employeId, dateEntree, onFermer, onTermine }) {
  const [f, setF] = useState({ qualite: 'CONFIRMEE', source: '', motif: '' });
  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <FenetreFormulaire titre="Qualifier la date d'entrée" Icone={CalendarClock} pret={f.motif.trim().length >= 3} libelleBouton="Enregistrer" onFermer={onFermer}
      onEnvoyer={async () => {
        await ecrireDroits(`/salaries/${employeId}/qualite-date-entree`, f);
        onTermine('Qualité de la date d\'entrée enregistrée.');
      }}>
      <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
        Date d'entrée enregistrée : <strong>{date(dateEntree)}</strong>. Elle n'est pas modifiée ici : vous dites seulement si elle est sûre.
        Pour la corriger, passez par le dossier du salarié.
      </p>
      <Champ libelle="Qualité" requis>
        <select className="rh-champ-saisie" value={f.qualite} onChange={maj('qualite')}>
          {QUALITES_SAISIE.map((k) => <option key={k} value={k}>{QUALITES_DATE[k]}</option>)}
        </select>
      </Champ>
      <Champ libelle="Source (contrat, registre, attestation…)"><input className="rh-champ-saisie" value={f.source} onChange={maj('source')} /></Champ>
      <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={2} value={f.motif} onChange={maj('motif')} /></Champ>
    </FenetreFormulaire>
  );
}

// ─── Historique d'une proposition (reprise, ancienneté, régularisation) ─────────────────────
function Proposition({ titre, lignes, statut, motifDecision, peutDecider, onDecider, children }) {
  return (
    <div className="rh-carte" style={{ padding: 12, margin: '0 0 8px' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <strong style={{ flex: '1 1 200px', minWidth: 0 }}>{titre}</strong>
        <BadgeCode table={STATUTS_PROPOSITION} code={statut} />
      </div>
      <Lignes lignes={lignes} />
      {children}
      {motifDecision && <p style={{ margin: '8px 0 0', fontSize: 14 }}>Motif de la décision : {motifDecision}</p>}
      {peutDecider && statut === 'PROPOSEE' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <Bouton petit variante="primaire" Icone={CheckCircle2} onClick={() => onDecider(true)}>Valider</Bouton>
          <Bouton petit variante="danger" Icone={XCircle} onClick={() => onDecider(false)}>Rejeter</Bouton>
        </div>
      )}
    </div>
  );
}

// ─── Sortie : aperçu à une date puis préparation (en jours) ──────────────────────────────────
function ListeDroitsSortie({ titre, droits }) {
  if (!droits || !droits.length) return null;
  return (
    <>
      <h3 style={{ fontSize: 15, margin: '12px 0 4px' }}>{titre}</h3>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
        {droits.map((d) => (
          <li key={d.id || d.periode_debut}>
            Droit {d.id ? `ouvert le ${date(d.ouverture)}` : `théorique du ${date(d.ouverture)}`} : {d.id ? `reste ${jours(d.restant)}` : jours(d.jours_total)}
            {d.echeance ? ` (à prendre avant le ${date(d.echeance)})` : ''}
          </li>
        ))}
      </ul>
    </>
  );
}

function SectionSortie({ employeId, peutAgir }) {
  const [dateSortie, setDateSortie] = useState(aujourdhui());
  const [apercu, setApercu] = useState(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [fenetre, setFenetre] = useState(false);
  const [resultat, setResultat] = useState('');
  const voir = async () => {
    setEnCours(true); setErreur(''); setResultat('');
    try { setApercu(await lireDroits(`/salaries/${employeId}/sortie`, { date: dateSortie })); } catch (e) { setErreur(messageErreur(e)); setApercu(null); }
    setEnCours(false);
  };
  if (!peutAgir) {
    return <BandeauInfo>La préparation d'une sortie demande le droit « Régulariser les droits à congé », et ne se fait jamais sur sa propre situation.</BandeauInfo>;
  }
  const a = apercu;
  return (
    <Carte titre="Sortie du salarié" Icone={LogOut}>
      <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
        Aperçu des droits restants à une date de sortie, en jours. Aucun montant n'est calculé ici : la valorisation revient à la paie.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ fontSize: 13, display: 'grid', gap: 4 }}>Date de sortie
          <input type="date" className="rh-champ-saisie" style={{ width: 'auto' }} value={dateSortie} onChange={(e) => { setDateSortie(e.target.value); setApercu(null); }} />
        </label>
        <Bouton Icone={Search} disabled={!dateSortie || enCours} onClick={voir}>Voir l'aperçu</Bouton>
      </div>
      {erreur && <div style={{ marginTop: 12 }}><BandeauInfo ton="danger">{erreur}</BandeauInfo></div>}
      {resultat && <div style={{ marginTop: 12 }}><BandeauInfo ton="succes">{resultat}</BandeauInfo></div>}
      {a && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            <strong>Situation au {date(a.date_sortie)}</strong><BadgeEtatCalcul qualite={a.qualite} />
          </div>
          {a.alerte && <BandeauInfo ton="alerte">La règle des années retenues à la rupture n'est pas validée : aucun droit n'est limité, à confirmer.</BandeauInfo>}
          {(a.blocages || []).length > 0 && (
            <BandeauInfo ton="danger" titre="Sortie non calculable">
              <ul style={{ margin: 0, paddingLeft: 18 }}>{a.blocages.map((b, i) => <li key={i}>{b.message}</li>)}</ul>
            </BandeauInfo>
          )}
          <Lignes lignes={[
            a.regle_rupture && ['Années retenues', `les ${nombre(a.regle_rupture.valeur)} dernière(s) année(s)${a.regle_rupture.reference ? ` (${a.regle_rupture.reference})` : ''}`],
            ['Droits ouverts restants', jours((a.droits_indemnisables || []).reduce((s, d) => s + Number(d.restant || 0), 0))],
            ['En cours d\'acquisition', a.en_cours ? `${jours(a.en_cours.jours)} depuis le ${date(a.en_cours.periode_debut)}` : 'Aucun'],
            ['Pris ou planifiés', jours(a.pris)],
          ]} />
          <ListeDroitsSortie titre="Droits retenus pour la sortie" droits={a.droits_indemnisables} />
          <ListeDroitsSortie titre="Droits plus anciens signalés (décision humaine)" droits={a.droits_anterieurs_signales} />
          <ListeDroitsSortie titre="Droits théoriques encore à confirmer" droits={a.droits_theoriques_a_confirmer} />
          {(a.reserves || []).length > 0 && <><h3 style={{ fontSize: 15, margin: '12px 0 4px' }}>Réserves</h3><ListeReserves reserves={a.reserves} /></>}
          <div style={{ marginTop: 12 }}>
            <Bouton variante="primaire" Icone={LogOut} disabled={(a.blocages || []).length > 0} onClick={() => setFenetre(true)}>Préparer la sortie</Bouton>
          </div>
        </div>
      )}
      {fenetre && (
        <FenetreMotif titre={`Préparer la sortie au ${date(dateSortie)}`} Icone={LogOut} libelleBouton="Préparer la sortie"
          aide="Les droits restants sont transmis à la préparation de la paie, en jours, pour être traités. Rien n'est payé ni supprimé ici."
          onFermer={() => setFenetre(false)}
          onConfirmer={async (motif) => {
            const r = await ecrireDroits(`/salaries/${employeId}/sortie`, { date_sortie: dateSortie, motif });
            const n = (r.faits || []).length;
            setFenetre(false);
            setResultat(n ? `Sortie préparée : ${n} élément${n > 1 ? 's' : ''} transmis à la préparation de la paie (en jours).` : 'Sortie préparée : aucun droit restant à transmettre.');
          }} />
      )}
    </Carte>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────────────────────
export default function FicheDroitsCongesPage() {
  const { employeId } = useParams();
  const { donnees: s, erreur, recharger } = useChargement(() => lireDroits(`/salaries/${employeId}`), [employeId]);
  const [onglet, setOnglet] = useState('droits');
  const [fenetre, setFenetre] = useState(null);
  const [message, setMessage] = useState('');
  const fini = async (m) => { setFenetre(null); setMessage(m); await recharger(); };

  if (!s) {
    return (
      <Layout>
        <div className="rh-page">
          <EnTetePage fil={[{ libelle: 'Congés', vers: '/conges' }, { libelle: 'Droits à congé', vers: '/conges/droits' }, { libelle: 'Salarié' }]} titre="Droits à congé" />
          {erreur ? <BandeauInfo ton="danger" titre="Affichage impossible">{erreur}</BandeauInfo> : <Squelette hauteur={240} />}
        </div>
      </Layout>
    );
  }

  const e = s.employe;
  const c = s.compteurs;
  const du = s.droits_utilisateur || {};
  const peutReprendre = !!du.reprendre && !du.sur_soi;
  const peutRegulariser = !!du.regulariser && !du.sur_soi;
  const theoriques = s.droits_theoriques || [];
  const droitDe = (id) => (s.droits || []).find((d) => d.id === id);
  const libelleDroit = (id) => { const d = droitDe(id); return d ? `Droit ouvert le ${date(d.ouverture)}` : (id ? 'Droit' : 'Non rattaché'); };
  const enAttente = (liste) => (liste || []).filter((x) => x.statut === 'PROPOSEE').length;
  const decider = (objet, id, valider, quoi) => setFenetre({ type: 'decision', objet, id, valider, quoi });
  const ea = s.en_acquisition;

  const onglets = [
    { cle: 'droits', libelle: 'Droits', Icone: CalendarRange, compte: theoriques.filter((t) => t.statut === 'A_CONFIRMER').length || undefined },
    { cle: 'registre', libelle: 'Registre', Icone: ListChecks },
    { cle: 'reprise', libelle: 'Reprise des soldes', Icone: History, compte: enAttente(s.reprises) || undefined },
    { cle: 'anciennete', libelle: 'Ancienneté reprise', Icone: UserCheck, compte: enAttente(s.anciennetes) || undefined },
    { cle: 'date', libelle: 'Date d\'entrée', Icone: CalendarClock },
    { cle: 'regularisations', libelle: 'Régularisations', Icone: Scale, compte: enAttente(s.regularisations) || undefined },
    { cle: 'sortie', libelle: 'Sortie', Icone: LogOut },
  ];

  return (
    <Layout>
      <div className="rh-page" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
        <EnTetePage fil={[{ libelle: 'Congés', vers: '/conges' }, { libelle: 'Droits à congé', vers: '/conges/droits' }, { libelle: e.nom }]}
          titre={e.nom}
          sousTitre={[e.matricule && `Matricule ${e.matricule}`, e.site, e.date_entree ? `Entré le ${date(e.date_entree)}` : 'Date d\'entrée absente', `situation au ${date(s.date_reference)}`].filter(Boolean).join(' · ')}
          actions={<BadgeEtatCalcul qualite={s.qualite} />} />

        {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
        {du.sur_soi && <BandeauInfo>Il s'agit de votre propre situation : vous pouvez la consulter, mais toute action revient à une autre personne habilitée.</BandeauInfo>}
        {(s.blocages || []).length > 0 && (
          <BandeauInfo ton="danger" titre="Droit non calculable">
            <ul style={{ margin: 0, paddingLeft: 18 }}>{s.blocages.map((b, i) => <li key={i}>{b.message}</li>)}</ul>
          </BandeauInfo>
        )}
        {(s.alertes || []).length > 0 && (
          <BandeauInfo ton="alerte" titre="À traiter">
            <ul style={{ margin: 0, paddingLeft: 18 }}>{s.alertes.map((a, i) => <li key={i}>{a.message ? enClair(a.message) : ALERTES[a.code]}</li>)}</ul>
          </BandeauInfo>
        )}
        {(s.reserves || []).length > 0 && (
          <BandeauInfo ton="info" titre="Calcul sous réserve">
            <ListeReserves reserves={s.reserves} />
          </BandeauInfo>
        )}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '12px 0 16px' }}>
          <Tuile libelle="En acquisition" valeur={jours(c.en_acquisition)} aide={ea?.ouverture_prevue ? `utilisables le ${date(ea.ouverture_prevue)}` : 'pas encore utilisables'} />
          <Tuile libelle="Droits ouverts" valeur={s.solde_a_verifier ? 'À vérifier' : jours(c.disponibles)} aide={s.solde_a_verifier ? 'aucun droit établi : reprise ou confirmation à faire' : 'total des droits ouverts, avant consommation'} />
          <Tuile libelle="Planifiés" valeur={jours(c.planifies)} aide={c.en_attente ? `+ ${jours(c.en_attente)} en attente de validation` : (c.prochain_depart ? `départ le ${date(c.prochain_depart)}` : 'congés validés à venir')} />
          <Tuile libelle="Déjà pris" valeur={jours(c.deja_pris)} />
          <Tuile libelle="Restants" valeur={s.solde_a_verifier ? 'À vérifier' : jours(c.restants)} ton={c.restants < 0 ? 'danger' : 'primaire'} aide="à prendre" />
          <Tuile libelle="Prochaine échéance" valeur={c.prochaine_echeance ? date(c.prochaine_echeance) : '—'} aide={c.prochaine_echeance ? 'à prendre au plus tard' : 'aucun droit en cours'} />
        </div>

        <Onglets onglets={onglets} actif={onglet} onChange={(o) => { setOnglet(o); setMessage(''); }} libelle="Sections de la fiche" />
        <div role="tabpanel" id={`panneau-${onglet}`} aria-labelledby={`onglet-${onglet}`}>

          {onglet === 'droits' && (
            <>
              <Carte titre="Droits ouverts" Icone={BadgeCheck}>
                {(s.droits || []).length === 0 ? <EtatVide titre="Aucun droit ouvert" texte="Les droits s'ouvrent après la période de service prévue par les règles, ou par la reprise des soldes." /> : (
                  <Defilant libelle="Droits ouverts">
                    <table className="rh-tableau" style={{ fontSize: 14, minWidth: 900 }}>
                      <thead><tr>
                        <th>Ouvert le</th><th>À prendre avant</th><th style={{ textAlign: 'right' }}>Droit</th><th style={{ textAlign: 'right' }}>Report</th>
                        <th style={{ textAlign: 'right' }}>Pris</th><th style={{ textAlign: 'right' }}>Reste</th><th>Statut</th><th aria-label="Actions" />
                      </tr></thead>
                      <tbody>{s.droits.map((d) => (
                        <tr key={d.id}>
                          <td>{date(d.ouverture)}<div className="rh-cellule-secondaire">{ORIGINES_DROIT[d.origine] || ''}{d.qualite ? ` · ${QUALITES_DROIT[d.qualite] || ''}` : ''}</div></td>
                          <td>{d.echeance ? date(d.echeance) : '—'}</td>
                          <td style={{ textAlign: 'right' }}>{jours(d.jours_total)}{d.regularise ? <div className="rh-cellule-secondaire">régularisé {signe(d.regularise)}</div> : null}</td>
                          <td style={{ textAlign: 'right' }}>{d.reporte_entrant ? `+${jours(d.reporte_entrant)}` : ''}{d.reporte_sortant ? ` −${jours(d.reporte_sortant)}` : ''}{!d.reporte_entrant && !d.reporte_sortant ? '—' : ''}</td>
                          <td style={{ textAlign: 'right' }}>{jours(d.pris)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{jours(d.restant)}</td>
                          <td><BadgeCode table={STATUTS_DROIT} code={d.statut} /></td>
                          <td>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <Bouton petit Icone={Calculator} onClick={() => setFenetre({ type: 'calcul', droit: d })}>Voir le calcul</Bouton>
                              {peutRegulariser && d.restant > 0 && (
                                <Bouton petit variante="discret" Icone={ArrowRightLeft} onClick={() => setFenetre({ type: 'report', droit: d })}>Constater un report</Bouton>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </Defilant>
                )}
              </Carte>

              {theoriques.length > 0 && (
                <Carte titre="Droits calculés, pas encore ouverts" Icone={CheckCircle2}>
                  <p style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
                    Un droit certain s'ouvre automatiquement. Un droit sous réserve attend la confirmation motivée d'une personne habilitée.
                  </p>
                  {theoriques.map((t) => (
                    <div key={t.periode_debut} className="rh-carte" style={{ padding: 12, margin: '0 0 8px' }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <strong style={{ flex: '1 1 220px', minWidth: 0 }}>{jours(t.jours_total)} à l'ouverture du {date(t.ouverture)}</strong>
                        {t.statut === 'A_CONFIRMER' ? <Badge ton="alerte" point>À confirmer</Badge> : <Badge ton="info" point>Ouverture automatique en attente</Badge>}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--sg-texte-2)', margin: '4px 0' }}>
                        Période du {date(t.periode_debut)} au {date(t.ouverture)}{t.echeance ? ` · à prendre avant le ${date(t.echeance)}` : ''}
                      </div>
                      <ListeReserves reserves={t.reserves} />
                      <details style={{ marginTop: 6 }}>
                        <summary style={{ cursor: 'pointer', fontSize: 14 }}>Pourquoi ce résultat ?</summary>
                        <div style={{ marginTop: 6 }}><Etapes etapes={t.explication} /></div>
                      </details>
                      {peutRegulariser && (
                        <div style={{ marginTop: 8 }}>
                          <Bouton petit variante="primaire" Icone={CheckCircle2} onClick={() => setFenetre({ type: 'confirmer', theorique: t })}>
                            {t.statut === 'A_CONFIRMER' ? 'Confirmer ce droit' : 'Enregistrer l\'ouverture'}
                          </Bouton>
                        </div>
                      )}
                    </div>
                  ))}
                </Carte>
              )}

              {s.periodes_anterieures_non_reprises > 0 && (
                <BandeauInfo titre="Périodes anciennes non reprises">
                  {s.periodes_anterieures_non_reprises === 1 ? '1 période plus ancienne, dont l\'échéance est dépassée, n\'a pas de droit enregistré'
                    : `${s.periodes_anterieures_non_reprises} périodes plus anciennes, dont l'échéance est dépassée, n'ont pas de droit enregistré`} :
                  {' '}leur solde relève de la reprise des soldes (rien n'est crédité ni effacé automatiquement).
                </BandeauInfo>
              )}

              <Carte titre="Période en cours d'acquisition" Icone={Hourglass}>
                {!ea ? <p style={{ margin: 0, fontSize: 14 }}>Aucune période en cours d'acquisition.</p> : (
                  <>
                    <Lignes lignes={[
                      ['Acquis à ce jour', `${jours(ea.jours)} (non utilisables avant l'ouverture)`],
                      ['Depuis le', date(ea.periode_debut)],
                      ['Service effectif', `${ea.mois_service} mois et ${ea.jours_mois_entame} jour${ea.jours_mois_entame > 1 ? 's' : ''}`],
                      ['Ouverture prévue', date(ea.ouverture_prevue)],
                      ['Droit prévu à l\'ouverture', jours(ea.prevu_a_l_ouverture)],
                      ['À prendre au plus tard', ea.echeance_prevue ? date(ea.echeance_prevue) : '—'],
                      ['État du calcul', <BadgeEtatCalcul key="q" qualite={ea.qualite} />],
                    ]} />
                    {(ea.non_appliquees || []).length > 0 && (
                      <p style={{ margin: '10px 0 0', fontSize: 14 }}>
                        Non accordé pour l'instant : {ea.non_appliquees.map((m) => `${m.libelle}${m.jours ? ` (${jours(m.jours)})` : ''} — ${(RESERVES[m.raison] || 'à vérifier').toLowerCase()}`).join(' ; ')}
                      </p>
                    )}
                    <ListeReserves reserves={ea.reserves} />
                    <details style={{ marginTop: 8 }} open>
                      <summary style={{ cursor: 'pointer', fontSize: 14 }}>Pourquoi ce résultat ?</summary>
                      <div style={{ marginTop: 6 }}><Etapes etapes={ea.explication} /></div>
                    </details>
                  </>
                )}
              </Carte>
            </>
          )}

          {onglet === 'registre' && (
            <Carte titre="Registre des mouvements" Icone={ListChecks}>
              {(s.mouvements || []).length === 0 ? <EtatVide titre="Aucun mouvement" texte="Le registre se remplit à l'ouverture des droits, à chaque congé pris et à chaque régularisation." /> : (
                <Defilant libelle="Registre des mouvements">
                  <table className="rh-tableau" style={{ fontSize: 14, minWidth: 720 }}>
                    <thead><tr><th>Date</th><th>Mouvement</th><th style={{ textAlign: 'right' }}>Jours</th><th>Droit</th></tr></thead>
                    <tbody>{s.mouvements.map((m) => (
                      <tr key={m.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{date(m.date_valeur)}</td>
                        <td>{NATURES[m.nature] || 'Mouvement'}{m.motif && <div className="rh-cellule-secondaire">{enClair(m.motif)}</div>}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: Number(m.jours) < 0 ? 'var(--sg-danger)' : 'var(--sg-succes)', whiteSpace: 'nowrap' }}>{signe(m.jours)}</td>
                        <td>{libelleDroit(m.droit_id)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </Defilant>
              )}
            </Carte>
          )}

          {onglet === 'reprise' && (
            <Carte titre="Reprise des soldes" Icone={History}
              action={peutReprendre && !enAttente(s.reprises) && !(s.reprises || []).some((r) => r.statut === 'VALIDEE')
                ? <Bouton petit variante="primaire" Icone={Plus} onClick={() => setFenetre({ type: 'reprise' })}>Proposer une reprise</Bouton> : null}>
              <p style={{ margin: '0 0 12px', fontSize: 14 }}>
                {e.reprise
                  ? `Reprise validée : situation au ${date(e.reprise.date_situation)}, période en cours depuis le ${date(e.reprise.debut_periode_en_cours)}. Une correction passe par une régularisation.`
                  : e.historique_complet
                    ? 'Le salarié est entré après la mise en place du suivi : son historique est complet, aucune reprise n\'est nécessaire.'
                    : 'L\'historique antérieur n\'est pas repris : les droits sont calculés sous réserve tant que la reprise n\'est pas validée.'}
              </p>
              {(s.reprises || []).length === 0 ? <EtatVide titre="Aucune reprise proposée" /> : s.reprises.map((r) => (
                <Proposition key={r.id} titre={`Reprise proposée le ${date(r.created_at)}`} statut={r.statut} motifDecision={r.motif_decision}
                  peutDecider={peutReprendre} onDecider={(v) => decider('reprises', r.id, v, 'la reprise')}
                  lignes={[
                    ['Situation au', date(r.date_situation)],
                    ['Période en cours depuis le', date(r.debut_periode_en_cours)],
                    ['Source', `${SOURCES_REPRISE[r.source] || 'Autre'}${r.reference ? ` — ${r.reference}` : ''}`],
                    r.ancien_solde !== null && r.ancien_solde !== undefined && ['Ancien solde du logiciel (pour information)', jours(r.ancien_solde)],
                    ['Motif', r.motif],
                  ]}>
                  <div style={{ marginTop: 8, fontSize: 14 }}>
                    {(r.droits || []).length === 0 ? 'Aucun droit ouvert non pris déclaré.' : (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {r.droits.map((d, i) => <li key={i}>{jours(d.jours)} ouverts le {date(d.ouverture)}{d.echeance ? `, à prendre avant le ${date(d.echeance)}` : ''}</li>)}
                      </ul>
                    )}
                  </div>
                </Proposition>
              ))}
            </Carte>
          )}

          {onglet === 'anciennete' && (
            <Carte titre="Ancienneté reprise" Icone={UserCheck}
              action={peutReprendre && !enAttente(s.anciennetes) && !(s.anciennetes || []).some((a) => a.statut === 'VALIDEE')
                ? <Bouton petit variante="primaire" Icone={Plus} onClick={() => setFenetre({ type: 'anciennete' })}>Proposer une décision</Bouton> : null}>
              <p style={{ margin: '0 0 12px', fontSize: 14 }}>
                {!e.anciennete && 'Ancienneté comptée depuis la date d\'entrée ; aucune décision n\'est attendue.'}
                {e.anciennete?.statut === 'CONFIRMEE' && (e.anciennete.decision === 'REPRISE'
                  ? `Ancienneté reprise depuis le ${date(e.anciennete.date_depart)} (${ORIGINES_ANCIENNETE[e.anciennete.origine] || 'autre origine'}).`
                  : 'Décision validée : pas de reprise d\'ancienneté, ancienneté comptée depuis l\'entrée.')}
                {e.anciennete?.statut === 'A_CONFIRMER' && `Ancienneté à confirmer : ${enClair(e.anciennete.motif)}. La majoration d'ancienneté n'est pas accordée en attendant.`}
              </p>
              {(s.anciennetes || []).length === 0 ? <EtatVide titre="Aucune décision proposée" /> : s.anciennetes.map((a) => (
                <Proposition key={a.id} titre={a.decision === 'REPRISE' ? `Reprise de l'ancienneté depuis le ${date(a.date_depart)}` : 'Pas de reprise d\'ancienneté'}
                  statut={a.statut} motifDecision={a.motif_decision} peutDecider={peutReprendre} onDecider={(v) => decider('anciennetes', a.id, v, 'la décision d\'ancienneté')}
                  lignes={[
                    ['Origine', ORIGINES_ANCIENNETE[a.origine] || 'Autre'],
                    ['Date d\'effet', date(a.date_effet)],
                    a.reference && ['Référence', a.reference],
                    ['Proposée le', date(a.created_at)],
                    ['Motif', a.motif],
                  ]} />
              ))}
            </Carte>
          )}

          {onglet === 'date' && (
            <Carte titre="Date d'entrée" Icone={CalendarClock}
              action={peutReprendre && e.date_entree ? <Bouton petit variante="primaire" Icone={CalendarClock} onClick={() => setFenetre({ type: 'qualite' })}>Qualifier la date</Bouton> : null}>
              <Lignes lignes={[
                ['Date d\'entrée enregistrée', e.date_entree ? date(e.date_entree) : 'Absente : à saisir dans le dossier du salarié'],
                ['Qualité', <BadgeQualiteDate key="q" q={e.qualite_date_entree} />],
                ['Origine de la qualité', e.qualite_date_entree?.source === 'QUALIFICATION_RH' ? 'Qualifiée par la RH' : 'Estimée automatiquement (non vérifiée)'],
                e.qualite_date_entree?.motif && ['Motif', e.qualite_date_entree.motif],
                (e.qualite_date_entree?.indices || []).length > 0 && ['Indices relevés', e.qualite_date_entree.indices.join(' ; ')],
              ]} />
              {(s.alertes || []).some((a) => a.code === 'DATE_ENTREE_MODIFIEE') && (
                <div style={{ marginTop: 12 }}>
                  <BandeauInfo ton="alerte">La date d'entrée a changé depuis sa qualification : qualifiez-la de nouveau, puis recherchez les écarts dans « Régularisations ».</BandeauInfo>
                </div>
              )}
              <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--sg-texte-2)' }}>
                La qualification ne modifie jamais la date : elle indique si le calcul peut s'y fier. Chaque qualification est motivée et tracée.
              </p>
            </Carte>
          )}

          {onglet === 'regularisations' && (
            <Carte titre="Régularisations" Icone={Scale}
              action={peutRegulariser ? (
                <Bouton petit Icone={Search} onClick={async () => {
                  try {
                    const r = await ecrireDroits(`/salaries/${employeId}/regularisations/detecter`);
                    await fini(r.length ? `${r.length} écart${r.length > 1 ? 's' : ''} trouvé${r.length > 1 ? 's' : ''} : chaque proposition doit être décidée par une autre personne habilitée.` : 'Aucun nouvel écart : les droits enregistrés correspondent aux données actuelles.');
                  } catch (x) { setMessage(''); setFenetre({ type: 'erreur', texte: messageErreur(x) }); }
                }}>Rechercher les écarts</Bouton>
              ) : null}>
              <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
                Un droit enregistré n'est jamais réécrit : si une donnée change (date d'entrée, ancienneté…), l'écart devient une proposition de régularisation.
              </p>
              {(s.regularisations || []).length === 0 ? <EtatVide titre="Aucune régularisation" /> : s.regularisations.map((g) => (
                <Proposition key={g.id} titre={`${libelleDroit(g.droit_id)} : ${signe(g.difference)}`} statut={g.statut} motifDecision={g.motif_decision}
                  peutDecider={peutRegulariser} onDecider={(v) => decider('regularisations', g.id, v, 'la régularisation')}
                  lignes={[
                    ['Cause', CAUSES_REGUL[g.cause] || 'Autre'],
                    ['Droit enregistré', jours(g.ancien_total)],
                    ['Droit recalculé', jours(g.nouveau_total)],
                    ['Écart', signe(g.difference)],
                    ['Détectée le', date(g.created_at)],
                  ]} />
              ))}
            </Carte>
          )}

          {onglet === 'sortie' && <SectionSortie employeId={employeId} peutAgir={peutRegulariser} />}
        </div>

        {fenetre?.type === 'erreur' && (
          <Fenetre titre="Opération impossible" Icone={XCircle} onFermer={() => setFenetre(null)}>
            <BandeauInfo ton="danger">{fenetre.texte}</BandeauInfo>
            <PiedFenetre><Bouton onClick={() => setFenetre(null)}>Fermer</Bouton></PiedFenetre>
          </Fenetre>
        )}
        {fenetre?.type === 'calcul' && <FenetreCalcul droit={fenetre.droit} onFermer={() => setFenetre(null)} />}
        {fenetre?.type === 'report' && (
          <FenetreMotif titre="Constater un report" Icone={ArrowRightLeft} min={10} libelleBouton="Constater le report"
            resume={<p style={{ margin: '0 0 12px', fontSize: 14 }}>Le reste du droit ouvert le {date(fenetre.droit.ouverture)}, soit <strong>{jours(fenetre.droit.restant)}</strong>, passe sur le droit suivant. Son échéance d'origine est conservée.</p>}
            aide="Indiquez l'accord de report (date et forme de l'accord avec le salarié)."
            onFermer={() => setFenetre(null)}
            onConfirmer={async (motif) => {
              const r = await ecrireDroits(`/droits/${fenetre.droit.id}/report`, { motif });
              await fini(`Report constaté : ${jours(r.jours)} reportés vers le droit suivant.`);
            }} />
        )}
        {fenetre?.type === 'confirmer' && (
          <FenetreMotif titre={fenetre.theorique.statut === 'A_CONFIRMER' ? 'Confirmer ce droit' : 'Enregistrer l\'ouverture'} Icone={CheckCircle2} min={10}
            resume={(
              <div style={{ marginBottom: 12, fontSize: 14 }}>
                <p style={{ margin: '0 0 6px' }}><strong>{jours(fenetre.theorique.jours_total)}</strong> ouverts le {date(fenetre.theorique.ouverture)}, enregistrés avec leurs réserves :</p>
                <ListeReserves reserves={fenetre.theorique.reserves} />
              </div>
            )}
            aide="Expliquez pourquoi ce droit peut être confirmé malgré ses réserves. Une fois confirmé, il est figé ; un écart ultérieur passera par une régularisation."
            onFermer={() => setFenetre(null)}
            onConfirmer={async (motif) => {
              const r = await ecrireDroits(`/salaries/${employeId}/confirmer-droit`, { periode_debut: fenetre.theorique.periode_debut, motif });
              await fini(`Droit enregistré : ${jours(r.jours_total)}.`);
            }} />
        )}
        {fenetre?.type === 'decision' && (
          <FenetreMotif titre={`${fenetre.valider ? 'Valider' : 'Rejeter'} ${fenetre.quoi}`} Icone={fenetre.valider ? CheckCircle2 : XCircle}
            variante={fenetre.valider ? 'primaire' : 'danger'} libelleBouton={fenetre.valider ? 'Valider' : 'Rejeter'}
            aide={fenetre.valider ? 'La décision revient à une autre personne que celle qui a proposé. Elle est tracée avec son motif.' : 'La proposition reste dans l\'historique avec votre motif.'}
            onFermer={() => setFenetre(null)}
            onConfirmer={async (motif) => {
              await ecrireDroits(`/${fenetre.objet}/${fenetre.id}/decision`, { valider: fenetre.valider, motif });
              await fini(fenetre.valider ? 'Décision validée.' : 'Proposition rejetée.');
            }} />
        )}
        {fenetre?.type === 'reprise' && <FenetreReprise employeId={employeId} onFermer={() => setFenetre(null)} onTermine={fini} />}
        {fenetre?.type === 'anciennete' && <FenetreAnciennete employeId={employeId} onFermer={() => setFenetre(null)} onTermine={fini} />}
        {fenetre?.type === 'qualite' && <FenetreQualiteDate employeId={employeId} dateEntree={e.date_entree} onFermer={() => setFenetre(null)} onTermine={fini} />}
      </div>
    </Layout>
  );
}
